import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import type { DraftVersionActions, DraftVersionState, DraftVersionStore } from './draft-version-store';
import { createDraftVersionStore } from './draft-version-store';

const DraftVersionStoreContext = createContext<StoreApi<DraftVersionStore> | null>(null);

type DraftVersionStoreProviderProps = PropsWithChildren<{
    readonly initialStore?: StoreApi<DraftVersionStore>;
}>;

export const DraftVersionStoreProvider: FunctionComponent<DraftVersionStoreProviderProps> = ({
    children,
    initialStore,
}) => {
    const storeRef = useRef<StoreApi<DraftVersionStore> | null>(null);

    storeRef.current ??= initialStore ?? createDraftVersionStore();

    return <DraftVersionStoreContext.Provider value={storeRef.current}>{children}</DraftVersionStoreContext.Provider>;
};

const useDraftVersionStoreContext = (): StoreApi<DraftVersionStore> => {
    const store = useContext(DraftVersionStoreContext);

    if (store === null) {
        throw new Error('useDraftVersionStore must be used within DraftVersionStoreProvider');
    }

    return store;
};

export const useDraftVersionState = (): DraftVersionState => {
    const store = useDraftVersionStoreContext();

    return useStore(
        store,
        useShallow((state) => ({
            drafts: state.drafts,
        })),
    );
};

export const useDraftVersionActions = (): DraftVersionActions => {
    const store = useDraftVersionStoreContext();

    return useMemo(
        () => ({
            addDraft: store.getState().addDraft,
            getDraft: store.getState().getDraft,
            hasDraft: store.getState().hasDraft,
            removeDraft: store.getState().removeDraft,
            updateDraft: store.getState().updateDraft,
        }),
        [store],
    );
};

export const useDraftVersionStore = (): StoreApi<DraftVersionStore> => {
    return useDraftVersionStoreContext();
};
